// Shared lab test structures used by Laboratory entry, Clinician view, and Admin view.

export const fbcStructure = [
  {
    category: 'WHITE BLOOD CELLS AND WBC MORPHOLOGY',
    tests: [
      { name: 'White Blood Cells (WBC)', range: '4-10', unit: 'x10^3/uL' },
      { name: 'Neutrophils Neu %', range: '40-70', unit: '%' },
      { name: 'Lymphocytes LYMP %', range: '20-45', unit: '%' },
      { name: 'Monocytes MONO %', range: '2-10', unit: '%' },
      { name: 'Basophils BASO %', range: '<1', unit: '%' },
      { name: 'Eosinophils EOS%', range: '<6', unit: '%' },
      { name: 'Neutrophils NEUT#', range: '1.7-7.0', unit: 'x10^3/uL' },
      { name: 'Lymphocyte LYM#', range: '1.0-3.0', unit: 'x10^3/uL' },
      { name: 'Monocyte MON #', range: '0.3-0.9', unit: 'x10^3/uL' },
      { name: 'Basophils BASO#', range: '<0.3', unit: 'x10^3/uL' },
      { name: 'Eosinophils EOS#', range: '0.05-0.5', unit: 'x10^3/uL' },
    ],
  },
  {
    category: 'RED CELLS AND RBC LINE',
    tests: [
      { name: 'Red Blood Cells (RBCs)', range: '4.3-5.572', unit: 'x10^6/uL' },
      { name: 'Haematocrit', range: '38.8-54', unit: '%' },
      { name: 'Haemoglobin', range: '11.5-18.5', unit: 'g/dl' },
      { name: 'MCV', range: '81.2-95.1', unit: 'fL' },
      { name: 'MCH', range: '26.6-33.3', unit: 'pg/cell' },
      { name: 'MCHC', range: '31.5-36.3', unit: 'g/dl' },
      { name: 'RDW-SD', range: '35-55', unit: 'fL' },
      { name: 'RDW-CV', range: '11.5-14.5', unit: '%' },
    ],
  },
  {
    category: 'PLATELETS',
    tests: [
      { name: 'Platelets (PLT)', range: '100-400', unit: 'x10^3/uL' },
      { name: 'PDW', range: '9-18.5', unit: '%' },
      { name: 'MPV', range: '7.65-11.5', unit: 'fL' },
      { name: 'P-LCR', range: '21-45', unit: '%' },
    ],
  },
]

export const urinalysisTests = [
  { name: 'LEU', fullName: 'Leukocytes' },
  { name: 'NIT', fullName: 'Nitrites' },
  { name: 'URO', fullName: 'Urobilinogen' },
  { name: 'PRO', fullName: 'Protein' },
  { name: 'pH', fullName: 'pH' },
  { name: 'BLO', fullName: 'Blood' },
  { name: 'SG', fullName: 'Specific Gravity' },
  { name: 'KET', fullName: 'Ketones' },
  { name: 'BIL', fullName: 'Bilirubin' },
  { name: 'GLU', fullName: 'Glucose' },
]

export const testosteroneTests = [
  {
    name: 'Testosterone, Total',
    rangeMale: '1.66-8.11',
    rangeFemale: '0.13-1.08',
    unit: 'ng/mL',
    method: 'Immunofluorescence',
  },
]

export interface HormoneTest {
  name: string
  key: string // storage key suffix (HP_<key>)
  ranges: { label: string; value: string }[]
  unit: string
  method: string
}

export const hormonalProfileTests: HormoneTest[] = [
  {
    name: 'Estradiol (E2)',
    key: 'E2',
    ranges: [
      { label: 'Follicular Phase', value: '20 - 146' },
      { label: 'Ovulatory Phase', value: '35 - 630' },
      { label: 'Luteal Phase', value: '35 - 630' },
      { label: 'Post-Menopausal', value: '< 140' },
    ],
    unit: 'pg/mL',
    method: 'Immunofluorescence',
  },
  {
    name: 'Luteinising Hormone (LH)',
    key: 'LH',
    ranges: [
      { label: 'Follicular Phase', value: '2.95 – 13.65' },
      { label: 'Midcycle', value: '13.65 – 95.75' },
      { label: 'Luteal Phase', value: '1.25 – 11.00' },
      { label: 'Post-Menopausal', value: '8.24 – 55.23' },
    ],
    unit: 'mIU/mL',
    method: 'Immunofluorescence Anbio',
  },
  {
    name: 'Prolactin (PRL)',
    key: 'PRL',
    ranges: [
      { label: 'Non-pregnant women', value: '4.60 – 25.07' },
      { label: 'Pregnant women', value: '10 – 209' },
    ],
    unit: 'ng/mL',
    method: 'Immunofluorescence Anbio',
  },
  {
    name: 'Progesterone (PROG)',
    key: 'PROG',
    ranges: [
      { label: 'Follicular Phase', value: '0.15 – 1.4' },
      { label: 'Luteal Phase', value: '2.0 – 25' },
      { label: '1st Trimester', value: '7.25 – 90.0' },
      { label: '2nd Trimester', value: '19.5 – 91.0' },
      { label: '3rd Trimester', value: '49.5 – 422.0' },
      { label: 'Post-Menopausal', value: '< 0.5' },
    ],
    unit: 'ng/mL',
    method: 'Immunofluorescence',
  },
  {
    name: 'Follicle Stimulating Hormone (FSH)',
    key: 'FSH',
    ranges: [
      { label: 'Follicular Phase', value: '4.46 – 12.43' },
      { label: 'Mid Cycle', value: '4.88 – 20.96' },
      { label: 'Luteal Phase', value: '1.96 – 7.70' },
      { label: 'Post-Menopausal', value: '22.70 – 130' },
    ],
    unit: 'mIU/mL',
    method: 'Immunofluorescence Anbio',
  },
]

export const isFBCTest = (testName: string) => {
  const t = testName.toLowerCase()
  return t === 'fbc' || t.includes('full blood count')
}

export const isUrinalysisTest = (testName: string) =>
  testName.toLowerCase().includes('urinalysis')

export const isTestosteroneTest = (testName: string) =>
  testName.toLowerCase().includes('testosterone')

export const isHormonalProfileTest = (testName: string) =>
  testName.toLowerCase().includes('hormonal profile')

// ─── Anti-Müllerian Hormone (AMH) ───
// Single-value test with age-banded reference ranges (ng/mL).
export interface AMHAgeRange {
  ageBand: string
  range: string
}

export const amhStructure = {
  name: 'Anti Mullerian Hormone (AMH)',
  storageKey: 'AMH_VALUE',
  unit: 'ng/mL',
  method: 'Serum, Anbio Immunofluorescence',
  ageRanges: [
    { ageBand: '20 – 24', range: '1.4 – 10.9' },
    { ageBand: '25 – 29', range: '0.9 – 8.9' },
    { ageBand: '30 – 34', range: '0.6 – 8.4' },
    { ageBand: '35 – 39', range: '0.2 – 7.0' },
    { ageBand: '40 – 44', range: '0.1 – 5.1' },
    { ageBand: '45 – 50', range: '0.1 – 3.1' },
  ] as AMHAgeRange[],
  interpretation: [
    'Results in the normal range indicate high fertility and ovarian reserve and a high chance of IVF success.',
    'Results in the low normal range indicate low fertility and ovarian reserve and a reduced but fair chance of IVF success.',
    'Results in the very low range indicate a very low ovarian reserve and low chances of reproductive success with IVF.',
    'High AMH levels are usually seen in PCOS and some AMH producing ovarian tumors.',
  ],
}

export const isAMHTest = (testName: string) => {
  const t = testName.toLowerCase()
  return (
    t.includes('anti-müllerian') ||
    t.includes('anti mullerian') ||
    t.includes('anti-mullerian') ||
    t.includes('antimüllerian') ||
    t === 'amh' ||
    t.includes(' amh') ||
    t.startsWith('amh ')
  )
}

// ─── Beta HCG (Quantitative Serum) ───
// Single quantitative value with stage-of-pregnancy reference ranges (mIU/mL).
export interface HCGRange {
  label: string
  range: string
}

export const hcgStructure = {
  name: 'Beta HCG Level (Quantitative)',
  storageKey: 'HCG_VALUE',
  unit: 'mIU/mL',
  method: 'Immunofluorescence',
  ranges: [
    { label: 'Normal Serum (non-pregnant)', range: '< 5' },
    { label: '1st Week pregnancy', range: '10 – 50' },
    { label: '2nd Week pregnancy', range: '20 – 500' },
    { label: '3rd Week pregnancy', range: '501 – 5,000' },
    { label: '4th Week pregnancy', range: '3,000 – 19,000' },
    { label: '5 – 8 Weeks pregnancy', range: '14,000 – 169,000' },
    { label: '9 – 13 Weeks pregnancy', range: '16,000 – 180,000' },
    { label: '22 Weeks pregnancy', range: '4,500 – 70,000' },
    { label: '23 Weeks pregnancy', range: '3,000 – 69,500' },
    { label: '3rd Trimester', range: '2,400 – 50,000' },
  ] as HCGRange[],
}

// Values above this threshold (mIU/mL) are flagged abnormal/red in all views.
// Per the reference range, normal non-pregnant serum is < 5; values > 6 are
// considered out-of-range and displayed in red.
export const HCG_ABNORMAL_THRESHOLD = 6

export const isHCGAbnormal = (value: string | number | undefined | null) => {
  if (value === undefined || value === null || value === '') return false
  const raw = typeof value === 'number' ? String(value) : value
  // Extract the first numeric value (handles "12.5", "12 mIU/mL", "> 100", etc.)
  const match = raw.match(/-?\d+(\.\d+)?/)
  if (!match) return false
  const n = parseFloat(match[0])
  if (Number.isNaN(n)) return false
  return n > HCG_ABNORMAL_THRESHOLD
}

export const isHCGTest = (testName: string) => {
  const t = testName.toLowerCase()
  return (
    t === 'hcg' ||
    t === 'beta hcg' ||
    t === 'β-hcg' ||
    t.includes('beta hcg') ||
    t.includes('b-hcg') ||
    t.includes('β-hcg') ||
    t.includes('hcg test') ||
    t.includes('hcg level') ||
    t.includes('hcg (')
  )
}
